import { X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import {
  COLLECTIONS,
  findByFileName,
  isSupportedFile,
  SUPPORTED_EXTENSIONS,
  UNSUPPORTED_HINT,
  type DocumentCollection,
  type LibraryDocument,
  type Tenant,
} from './api';
import {
  useProperties,
  useReplaceDocument,
  useTenants,
  useUploadDocument,
  useUploadReports,
  type BatchItem,
} from './queries';

const MAX_BYTES = 100 * 1024 * 1024; // sreg-api's limit

/** The tenant choice for a document that belongs to no tenant. */
export const WHOLE_PROPERTY = 'whole-property';

const STATUS_SUFFIX: Partial<Record<Tenant['status'], string>> = {
  former: ' (former)',
  future: ' (future)',
  non_tenant_occupant: ' (occupant)',
};

export const tenantLabel = (t: Tenant) => t.canonicalName + (STATUS_SUFFIX[t.status] ?? '');

/** Lease documents belong to a tenant (or, chosen explicitly, the whole property); reports to the property. */
export const needsTenant = (c: DocumentCollection) => c === 'lease_documents';

/** Lease documents: one file, with its property and tenant. Reports: several files at once, for one property. */
export function UploadPanel({ collection, onClose }: { collection: DocumentCollection; onClose: () => void }) {
  const [target, setTarget] = useState<DocumentCollection>(collection);
  const collectionField = (
    <label className="text-xs font-semibold text-ink-soft">
      Collection
      <Select value={target} onChange={(e) => setTarget(e.target.value as DocumentCollection)}>
        {COLLECTIONS.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </Select>
    </label>
  );
  return (
    <div className="mt-4 rounded-xl border border-line bg-surface p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">
          {target === 'lease_reports' ? 'Upload reports' : 'Upload a document'}
        </h2>
        <button type="button" onClick={onClose} aria-label="Close" className="text-ink-faint hover:text-ink">
          <X size={16} />
        </button>
      </div>
      {target === 'lease_reports' ? (
        <ReportsUpload collectionField={collectionField} onClose={onClose} />
      ) : (
        <SingleUpload target={target} collectionField={collectionField} onClose={onClose} />
      )}
    </div>
  );
}

function PropertyField({ value, onChange }: { value: string; onChange: (key: string) => void }) {
  const properties = useProperties();
  return (
    <label className="text-xs font-semibold text-ink-soft">
      Property
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select a property</option>
        {properties.data?.map((p) => (
          <option key={p.key} value={p.key}>
            {p.name}
          </option>
        ))}
      </Select>
    </label>
  );
}

const OUTCOME: Record<BatchItem['outcome'], string> = {
  waiting: 'Waiting',
  uploading: 'Uploading…',
  created: 'Added',
  replaced: 'Replaced',
  failed: 'Failed',
  skipped: 'Skipped',
};

function ReportsUpload({ collectionField, onClose }: { collectionField: ReactNode; onClose: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [propertyKey, setPropertyKey] = useState('');
  const [items, setItems] = useState<BatchItem[]>([]);
  const upload = useUploadReports();
  const unsupported = files.filter((f) => !isSupportedFile(f.name));
  const finished = items.length > 0 && !upload.isPending;

  const start = () => {
    setItems(files.map((file) => ({ file, outcome: 'waiting' })));
    upload.mutate({
      files,
      propertyKey,
      onItem: (i, item) => setItems((cur) => cur.map((x, j) => (j === i ? item : x))),
    });
  };

  return (
    <>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="text-xs font-semibold text-ink-soft sm:col-span-3">
          Files
          <input
            type="file"
            multiple
            accept={SUPPORTED_EXTENSIONS.join(',')}
            onChange={(e) => {
              setFiles(Array.from(e.target.files ?? []));
              setItems([]);
            }}
            className="mt-1 block w-full text-sm text-ink file:mr-3 file:rounded-lg file:border-0 file:bg-surface-sunken file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-ink"
          />
        </label>
        <PropertyField value={propertyKey} onChange={setPropertyKey} />
        <div className="hidden sm:block" />
        {collectionField}
      </div>
      <p className="mt-2 text-xs text-ink-faint">
        A report with the same file name as one already here replaces it as a new revision. Then Process the month on
        the Periods page.
      </p>
      {unsupported.length > 0 && (
        <p className="mt-2 text-xs text-bronze">
          {UNSUPPORTED_HINT} Not uploaded: {unsupported.map((f) => f.name).join(', ')}
        </p>
      )}
      {items.length > 0 && (
        <ul
          className="mt-3 divide-y divide-line-soft rounded-lg border border-line text-sm"
          aria-label="Upload progress"
        >
          {items.map((it) => (
            <li key={it.file.name} className="flex items-center justify-between gap-3 px-3 py-1.5">
              <span className="truncate text-ink">{it.file.name}</span>
              <span
                className={`shrink-0 text-xs ${it.outcome === 'failed' || it.outcome === 'skipped' ? 'text-bronze' : 'text-ink-soft'}`}
              >
                {OUTCOME[it.outcome]}
                {it.detail ? ` · ${it.detail}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>
          {finished ? 'Done' : 'Close'}
        </Button>
        <Button onClick={start} disabled={!files.length || !propertyKey || upload.isPending}>
          Upload {files.length > 1 ? `${files.length} files` : ''}
        </Button>
      </div>
    </>
  );
}

function SingleUpload({
  target,
  collectionField,
  onClose,
}: {
  target: DocumentCollection;
  collectionField: ReactNode;
  onClose: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [propertyKey, setPropertyKey] = useState('');
  const [tenant, setTenant] = useState(''); // '' = not chosen yet
  const tenants = useTenants(propertyKey);
  const placed = !!propertyKey && (!needsTenant(target) || !!tenant);
  const partyId = needsTenant(target) && tenant !== WHOLE_PROPERTY ? tenant : undefined;
  const [step, setStep] = useState<'idle' | 'starting' | 'uploading' | 'indexing'>('idle');
  const upload = useUploadDocument();
  const replace = useReplaceDocument();
  const tooBig = !!file && file.size > MAX_BYTES;
  const unsupported = !!file && !isSupportedFile(file.name);
  const blocked = tooBig || unsupported;

  // Same file name already in this collection? Offer replace instead of a duplicate.
  const [existing, setExisting] = useState<LibraryDocument | null>(null);
  useEffect(() => {
    setExisting(null);
    if (!file || !isSupportedFile(file.name)) return;
    let stale = false;
    findByFileName(target, file.name)
      .then((rows) => !stale && setExisting(rows[0] ?? null))
      .catch(() => undefined); // the check is advisory; never blocks an upload
    return () => {
      stale = true;
    };
  }, [file, target]);

  const replaceExisting = () => {
    if (!file || !existing) return;
    setStep('uploading');
    replace.mutate(
      { id: existing.id, file },
      {
        onSuccess: (doc) => {
          setStep('idle');
          if (doc.status === 'indexed') onClose();
        },
        onError: () => setStep('idle'),
      },
    );
  };

  const start = () => {
    if (!file || !placed) return;
    setStep('starting');
    upload.mutate(
      { file, name, collection: target, propertyKey, partyId, onStep: setStep },
      {
        onSuccess: (doc) => {
          setStep('idle');
          if (doc.status === 'indexed') onClose();
        },
        onError: () => setStep('idle'),
      },
    );
  };

  const result = upload.data ?? replace.data;

  return (
    <>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="text-xs font-semibold text-ink-soft sm:col-span-3">
          File
          <input
            type="file"
            accept={SUPPORTED_EXTENSIONS.join(',')}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
            }}
            className="mt-1 block w-full text-sm text-ink file:mr-3 file:rounded-lg file:border-0 file:bg-surface-sunken file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-ink"
          />
        </label>
        <label className="text-xs font-semibold text-ink-soft sm:col-span-2">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Fidelity 3rd Amendment"
            className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-sage"
          />
        </label>
        <PropertyField
          value={propertyKey}
          onChange={(k) => {
            setPropertyKey(k);
            setTenant('');
          }}
        />
        {needsTenant(target) && (
          <label className="text-xs font-semibold text-ink-soft sm:col-span-2">
            Tenant
            <Select value={tenant} onChange={(e) => setTenant(e.target.value)} disabled={!propertyKey}>
              <option value="">{propertyKey ? 'Select a tenant' : 'Select a property first'}</option>
              <option value={WHOLE_PROPERTY}>Whole property (no tenant)</option>
              {tenants.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {tenantLabel(t)}
                </option>
              ))}
            </Select>
          </label>
        )}
        {collectionField}
      </div>
      {tooBig && <p className="mt-2 text-xs text-bronze">That file is over 100 MB.</p>}
      {unsupported && <p className="mt-2 text-xs text-bronze">{UNSUPPORTED_HINT}</p>}
      {existing && !blocked && (
        <div
          role="status"
          className="mt-3 rounded-lg border border-bronze-light bg-bronze-light/20 p-3 text-sm text-ink"
        >
          <p>
            <span className="font-semibold">“{existing.originalFileName}”</span> is already here as{' '}
            <span className="font-semibold">{existing.name}</span> (revision {existing.revision}).
          </p>
          <p className="mt-0.5 text-xs text-ink-soft">
            Replace it with this file as revision {existing.revision + 1}, or upload this as a separate document.
          </p>
          <div className="mt-2 flex gap-2">
            <Button onClick={replaceExisting} disabled={step !== 'idle'}>
              Replace existing
            </Button>
            <Button variant="secondary" onClick={start} disabled={!placed || step !== 'idle'}>
              Upload as new
            </Button>
          </div>
        </div>
      )}
      {result?.status === 'failed' && (
        <p className="mt-2 text-xs text-bronze">
          Stored, but indexing failed: {result.indexError}. You can retry from the list.
        </p>
      )}
      <div className="mt-4 flex items-center justify-end gap-3">
        {step !== 'idle' && (
          <span className="text-xs text-ink-faint">
            {step === 'starting' ? 'Preparing…' : step === 'uploading' ? 'Uploading…' : 'Indexing for search…'}
          </span>
        )}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {!existing && (
          <Button onClick={start} disabled={!file || blocked || !placed || step !== 'idle'}>
            Upload
          </Button>
        )}
      </div>
    </>
  );
}
